import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import { 
    COMPANY_NAME, 
    COMPANY_LOGO_PATH, 
    REPORT_FOOTER_TEXT 
} from '../appsettings';

// Create styles
const styles = StyleSheet.create({
    page: { padding: 30, fontFamily: 'Helvetica', fontSize: 12, color: '#333' },
    headerSection: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 20, borderBottomWidth: 1, borderBottomColor: '#ddd', paddingBottom: 10 },
    title: { fontSize: 24, fontWeight: 'bold', color: '#1890ff', marginBottom: 5 },
    subTitle: { fontSize: 10, color: '#666' },
    logo: { width: 50, height: 50 },
    sectionTitle: { fontSize: 16, fontWeight: 'bold', marginTop: 15, marginBottom: 10, color: '#333', borderBottomWidth: 1, borderBottomColor: '#eee', paddingBottom: 5 },
    row: { flexDirection: 'row', marginBottom: 5 },
    label: { width: 100, fontWeight: 'bold', color: '#555', fontSize: 10 },
    value: { flex: 1, fontSize: 10 },
    column: { flexDirection: 'column', width: '50%' },
    table: { display: "table", width: "auto", borderStyle: "solid", borderWidth: 1, borderColor: '#bfbfbf', marginTop: 10, marginBottom: 10 },
    tableRow: { margin: "auto", flexDirection: "row" },
    tableColHeader: { width: "20%", borderStyle: "solid", borderWidth: 1, borderLeftWidth: 0, borderTopWidth: 0, borderColor: '#bfbfbf', backgroundColor: '#f0f0f0', padding: 5 },
    tableCol: { width: "20%", borderStyle: "solid", borderWidth: 1, borderLeftWidth: 0, borderTopWidth: 0, borderColor: '#bfbfbf', padding: 5 },
    tableCellHeader: { margin: "auto", marginTop: 5, fontSize: 10, fontWeight: 'bold' },
    tableCell: { margin: "auto", marginTop: 5, fontSize: 9 },
    footer: { position: 'absolute', bottom: 30, left: 30, right: 30, textAlign: 'center', fontSize: 8, color: '#999', borderTopWidth: 1, borderTopColor: '#eee', paddingTop: 10 },
    
    // New List Item Styles
    listContainer: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
    listItem: { width: '48%', marginBottom: 10, padding: 10, backgroundColor: '#f9f9f9', borderRadius: 4, borderWidth: 1, borderColor: '#eee' },
    listItemTitle: { fontSize: 11, fontWeight: 'bold', marginBottom: 4, color: '#1890ff' },
    listItemContent: { fontSize: 10, color: '#555', marginBottom: 2 }
});

const MemberReportPDF = ({ member, assignedTrainers, showMetrics, showAssignments }) => {
    if (!member) return <Document><Page><Text>No Data</Text></Page></Document>;

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        return new Date(dateString).toLocaleString();
    };

    return (
        <Document title={`Report_${member.name}_${new Date().toLocaleString()}`} author={COMPANY_NAME}>
            <Page size="A4" style={styles.page}>
                
                {/* Header - Fixed Typo here */}
                <View style={styles.headerSection}>
                    <View>
                        <Text style={styles.title}>Member Profile</Text>
                        <Text style={styles.subTitle}>Generated on {new Date().toLocaleString()}</Text>
                    </View>
                    <View>
                        {/* Ensure /sitelogo.png exists in your public folder */}
                        <Image source={{ uri: COMPANY_LOGO_PATH }} style={styles.logo} />
                    </View>
                </View>

                {/* Personal Information */}
                <Text style={styles.sectionTitle}>Personal Details</Text>
                <View style={{ flexDirection: 'row' }}>
                    <View style={styles.column}>
                        <View style={styles.row}>
                            <Text style={styles.label}>Full Name:</Text>
                            <Text style={styles.value}>{member.name}</Text>
                        </View>
                        <View style={styles.row}>
                            <Text style={styles.label}>NIC Number:</Text>
                            <Text style={styles.value}>{member.nic}</Text>
                        </View>
                        <View style={styles.row}>
                            <Text style={styles.label}>Gender:</Text>
                            <Text style={styles.value}>{member.gender === 'M' ? 'Male' : 'Female'}</Text>
                        </View>
                        <View style={styles.row}>
                            <Text style={styles.label}>Date of Birth:</Text>
                            <Text style={styles.value}>{member.birthday}</Text>
                        </View>
                    </View>
                    <View style={styles.column}>
                        <View style={styles.row}>
                            <Text style={styles.label}>Contact No:</Text>
                            <Text style={styles.value}>{member.contact_no}</Text>
                        </View>
                         <View style={styles.row}>
                            <Text style={styles.label}>Address:</Text>
                            <Text style={styles.value}>{member.address}</Text>
                        </View>
                        <View style={styles.row}>
                            <Text style={styles.label}>Status:</Text>
                            <Text style={{...styles.value, color: Number(member.status) === 1 ? 'green' : 'red'}}>
                                {Number(member.status) === 1 ? 'Active' : 'Inactive'}
                            </Text>
                        </View>
                    </View>
                </View>

                 {/* Assigned Trainers Section - Optional Table Layout */}
                 {showAssignments && assignedTrainers && assignedTrainers.length > 0 && (
                    <>
                        <Text style={styles.sectionTitle}>Assigned Trainers</Text>
                        <View style={styles.table}>
                            <View style={styles.tableRow}>
                                <View style={{...styles.tableColHeader, width: '30%'}}><Text style={styles.tableCellHeader}>Name</Text></View>
                                <View style={{...styles.tableColHeader, width: '30%'}}><Text style={styles.tableCellHeader}>Contact</Text></View>
                                <View style={{...styles.tableColHeader, width: '40%'}}><Text style={styles.tableCellHeader}>NIC</Text></View>
                            </View>
                            {assignedTrainers.map((trainer, index) => (
                                <View style={styles.tableRow} key={index}>
                                    <View style={{...styles.tableCol, width: '30%'}}><Text style={styles.tableCell}>{trainer.name}</Text></View>
                                    <View style={{...styles.tableCol, width: '30%'}}><Text style={styles.tableCell}>{trainer.contact_no}</Text></View>
                                    <View style={{...styles.tableCol, width: '40%'}}><Text style={styles.tableCell}>{trainer.nic}</Text></View>
                                </View>
                            ))}
                        </View>
                    </>
                 )}


                {/* Physical Stats (Latest) */}
                <Text style={styles.sectionTitle}>Current Physical Stats</Text>
                <View style={{ flexDirection: 'row' }}>
                    <View style={styles.column}>
                        <View style={styles.row}>
                            <Text style={styles.label}>Weight:</Text>
                            <Text style={styles.value}>{member.weight ? `${member.weight} kg` : 'N/A'}</Text>
                        </View>
                        <View style={styles.row}>
                            <Text style={styles.label}>Height:</Text>
                            <Text style={styles.value}>{member.height ? `${member.height} cm` : 'N/A'}</Text>
                        </View>
                    </View>
                    <View style={styles.column}>
                         <View style={styles.row}>
                            <Text style={styles.label}>BMI:</Text>
                            <Text style={styles.value}>{member.bmi ? parseFloat(member.bmi).toFixed(2) : 'N/A'}</Text>
                        </View>
                        <View style={styles.row}>
                            <Text style={styles.label}>Body Fat:</Text>
                            <Text style={styles.value}>{member.body_fat ? `${member.body_fat}%` : 'N/A'}</Text>
                        </View>
                    </View>
                </View>

                {/* Metrics History Table - Conditional */}
                {showMetrics && (
                    <>
                        <Text style={styles.sectionTitle}>Metrics History</Text>
                        
                        <View style={styles.table}>
                            {/* Table Header */}
                            <View style={styles.tableRow}>
                                <View style={styles.tableColHeader}><Text style={styles.tableCellHeader}>Date</Text></View>
                                <View style={styles.tableColHeader}><Text style={styles.tableCellHeader}>Weight (kg)</Text></View>
                                <View style={styles.tableColHeader}><Text style={styles.tableCellHeader}>BMI</Text></View>
                                <View style={styles.tableColHeader}><Text style={styles.tableCellHeader}>Fat (%)</Text></View>
                                <View style={styles.tableColHeader}><Text style={styles.tableCellHeader}>Muscle (kg)</Text></View>
                            </View>

                            {/* Table Body - Mapping metrics */}
                            {member.metric && member.metric.length > 0 ? (
                                member.metric.map((row, index) => (
                                    <View style={styles.tableRow} key={index}>
                                        <View style={styles.tableCol}>
                                            <Text style={styles.tableCell}>{formatDate(row.date)}</Text>
                                        </View>
                                        <View style={styles.tableCol}>
                                            <Text style={styles.tableCell}>{row.weight}</Text>
                                        </View>
                                        <View style={styles.tableCol}>
                                            <Text style={styles.tableCell}>{row.bmi ? parseFloat(row.bmi).toFixed(2) : '-'}</Text>
                                        </View>
                                        <View style={styles.tableCol}>
                                            <Text style={styles.tableCell}>{row.body_fat}</Text>
                                        </View>
                                        <View style={styles.tableCol}>
                                            <Text style={styles.tableCell}>{row.muscle_mass}</Text>
                                        </View>
                                    </View>
                                ))
                            ) : (
                                <View style={styles.tableRow}>
                                    <View style={{...styles.tableCol, width: '100%'}}>
                                        <Text style={styles.tableCell}>No historical metrics found.</Text>
                                    </View>
                                </View>
                            )}
                        </View>
                    </>
                )}

                <Text style={styles.footer}>
                    Confidential Report • {member.name} • {member.nic} • {REPORT_FOOTER_TEXT}
                </Text>
            </Page>
        </Document>
    );
};

export default MemberReportPDF;